import React, { useEffect, useState } from "react";
import { api, apiErr, fileUrl } from "@/lib/api";
import { Button, Input, Label, Card } from "@/components/kit";
import { Upload, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { api.get("/company").then((r) => setForm(r.data)).catch((e) => toast.error(apiErr(e))); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/company", {
        name: form.name || "", org_number: form.org_number || "", address: form.address || "",
        email: form.email || "", phone: form.phone || "", logo_path: form.logo_path || "",
        app_url: form.app_url || "",
        currency: form.currency || "SEK", vat_rate: Number(form.vat_rate) || 0,
      });
      setForm(data); toast.success("Företagsuppgifter sparade");
    } catch (err) { toast.error(apiErr(err)); }
  };

  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_path: data.path }));
      toast.success("Logotyp uppladdad – kom ihåg att spara");
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };

  if (!form) return <div className="text-slate-400">Laddar…</div>;

  return (
    <div className="rise max-w-3xl">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-amber-500">Inställningar</p>
        <h1 className="font-display text-3xl font-extrabold text-white">Företag</h1>
        <p className="mt-1 text-sm text-slate-400">Visas på fakturaunderlag och attestsidan.</p>
      </div>

      <Card className="p-6">
        <form onSubmit={save} className="space-y-5">
          <div className="flex flex-wrap items-center gap-5">
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-xl border border-slate-700 bg-slate-950/60">
              {form.logo_path ? <img src={fileUrl(form.logo_path)} alt="logo" className="h-full w-full object-contain p-2" />
                : <Building2 size={30} className="text-slate-600" />}
            </div>
            <div>
              <Label>Logotyp</Label>
              <label data-testid="logo-upload-label" className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/40 px-4 py-2.5 text-sm text-slate-200 hover:border-amber-500 hover:text-amber-400">
                <Upload size={15} /> {uploading ? "Laddar upp…" : "Välj bild"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} />
              </label>
              <p className="mt-1.5 text-xs text-slate-500">PNG eller JPG. Visas på PDF-fakturan.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><Label>Företagsnamn</Label><Input data-testid="company-name-input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Org.nummer</Label><Input value={form.org_number || ""} onChange={(e) => setForm({ ...form, org_number: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Adress</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>E-post</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Telefon</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Valuta</Label><Input value={form.currency || "SEK"} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
            <div><Label>Momssats (%)</Label><Input data-testid="company-vat-input" type="number" step="0.1" value={form.vat_rate ?? 25} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} /></div>
            <div className="sm:col-span-2">
              <Label>App-adress (för attestlänkar)</Label>
              <Input data-testid="company-appurl-input" value={form.app_url || ""} onChange={(e) => setForm({ ...form, app_url: e.target.value })} placeholder="https://din-domän.se" />
              <p className="mt-1 text-xs text-slate-500">Används i länken som mejlas till beställaren. Sätt till din egen domän efter driftsättning (lämna tomt för att använda standarddomänen).</p>
            </div>
          </div>

          <Button data-testid="save-company-button" type="submit">Spara företagsuppgifter</Button>
        </form>
      </Card>
    </div>
  );
}
